// polyfills
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import 'regenerator-runtime/runtime'
import 'raf/polyfill'
/* Styles */
import '@foodsby/nutrient/src/css/app.css'
import './styles/index.css'

import { themeConWeb } from '@foodsby/nutrient'
import { refreshToken as refreshTokenApi } from '@foodsby/webapp-jwt'
import { ThemeProvider } from '@material-ui/core/styles'
import * as Sentry from '@sentry/browser'
import appboy from 'appboy-web-sdk' // Braze used to be called appboy
import { ConnectedRouter } from 'connected-react-router'
import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'
// on to the fun
import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { Route } from 'react-router'
import { Switch } from 'react-router-dom'
import { ThemeProvider as ThemeProviderStyled } from 'styled-components'

import pkg from '../package.json'
import { setWebpSupported } from '../src/redux/modules/browser'
import { isWebpSupported } from '../src/util/fileUtils'
import { FOODSBY_BASE_URL } from './api/api'
import CheckoutSidebar from './components/checkout/CheckoutSidebar'
import FriendRequest from './components/common/FriendRequest'
import HomeRedirect from './components/home/HomeRedirect'
import PrivacyPolicyPage from './components/legal/PrivacyPolicyPage'
import TermsOfUsePage from './components/legal/TermsOfUsePage'
import TermsOfUseRewardsPage from './components/legal/TermsOfUseRewardsPage'
import Logout from './components/login/Logout'
import NotFound from './components/not-found/NotFound'
import ProfileSidebar from './components/profile/ProfileSidebar'
import theme from './createMuiTheme'
import {
  PageChangePassword,
  PageCheckout,
  PageCompleteAccount,
  PageConfirmation,
  PageContact,
  PageCredits,
  PageForgotPassword,
  PageFriends,
  PageGiftRedemption,
  PageHome,
  PageLogin,
  PageMaintenance,
  PageNoLocation,
  PageOrderHistory,
  PagePlaceOrder,
  PageProfile,
  PageReferral,
  PageReferralTermsAndConditions,
  PageSelectLocation,
  PageSlack,
  PageSubscriptionTermsAndConditions,
  PageUI,
  PageUnsubscribe,
} from './pages'
import { ProviderCheckout } from './providers/ProviderCheckout'
import store, { history } from './redux/store'
import {
  FooterLayoutRoute,
  PublicLayoutRoute,
  RouteLayoutPrivate,
  SidebarLayoutRoute,
  SimpleLayoutRoute,
} from './routes/layoutRoutes'
import {
  changePasswordRoute,
  checkoutRoute,
  completeAccountRoute,
  confirmationRoute,
  contactRoute,
  creditsRoute,
  forgotPasswordRoute,
  friendsRoute,
  historyRoute,
  homeRoute,
  locationRoute,
  loginRoute,
  logoutRoute,
  maintenanceRoute,
  noLocationRoute,
  placeOrderRoute,
  privacyPolicyRoute,
  profileRoute,
  redeemGiftRoute,
  referralRoute,
  referralTermsAndConditionsRoute,
  rootRoute,
  selectKioskRoute,
  selectLocationRoute,
  slackRoute,
  subscriptionTermsAndConditionsRoute,
  termsAndConditionsRewardsRoute,
  termsAndConditionsRoute,
  uiRoute,
  unsubscribeRoute,
} from './routes/routes'
import { logException } from './util/errorUtils'
import { initTokens, isWithoutAccessToken } from './util/webStorageUtils'

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET

function inIframe() {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

// set up JWT
initTokens()

// Setup sentry
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  release: `${pkg.name}@${pkg.version}`,
})

// set up logrocket
if (process.env.REACT_APP_LOGROCKET_ID) {
  LogRocket.init(process.env.REACT_APP_LOGROCKET_ID, {
    console: {
      shouldAggregateConsoleErrors: true,
    },
    network: {
      requestSanitizer: function(req) {
        if (req.headers['Authorization']) {
          // remove token
          req.headers['Authorization'] = 'REDACTED'
        }
        if (req.url.indexOf('heartlandportico') > -1) {
          // remove heartland
          return null
        }
        if (req.url.indexOf('/oauth/token') > -1 || req.url.indexOf('/uaa/passwords') > -1) {
          // Remove tokens/passwords
          req.body = 'REDACTED'
        }
        return req
      },
      responseSanitizer: function(res) {
        if (res.url.indexOf('/oauth/token') > -1 || res.url.indexOf('/uaa/passwords') > -1) {
          // Remove tokens/passwords
          res.body = 'REDACTED'
        }
        return res
      },
    },
    release: `${pkg.name}@${pkg.version}`,
  })
  setupLogRocketReact(LogRocket)
}
LogRocket.getSessionURL(sessionURL => {
  Sentry.configureScope(scope => {
    scope.setExtra('sessionURL', sessionURL)
  })
})

// Update the old service worker if found
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    // returns installed service workers
    if (registrations.length) {
      // eslint-disable-next-line
      for (let registration of registrations) {
        registration.update()
      }
    }
  })
}

// Setup appboy
appboy.initialize(process.env.REACT_APP_BRAZE_API_KEY, {
  baseUrl: 'https://sdk.iad-03.braze.com/api/v3',
  enableHtmlInAppMessages: true,
  enableLogging: process.env.NODE_ENV !== 'production',
  minimumIntervalBetweenTriggerActionsInSeconds: 15,
  safariWebsitePushId: 'web.com.foodsby.order',
})
appboy.display.automaticallyShowNewInAppMessages()
appboy.openSession()
window.appboy = appboy

async function renderApp() {
  try {
    // Refresh JWT if needed
    if (isWithoutAccessToken()) {
      await refreshTokenApi(
        {
          clientId: CLIENT_ID,
          clientSecret: CLIENT_SECRET,
        },
        {
          baseURL: FOODSBY_BASE_URL,
        },
      )
    }

    // Setup webp support
    const hasWebpSupport = await isWebpSupported()
    store.dispatch(setWebpSupported(hasWebpSupport))

    // state
    const initialState = {
      subscription: { value: false },
    }

    const reducer = (state, action) => {
      switch (action.type) {
        case 'setSubscription':
          return {
            ...state,
            subscription: action.newSubscription,
          }

        default:
          return state
      }
    }

    // mount app
    const root = document.getElementById('root')
    if (root) {
      ReactDOM.render(
        <ThemeProviderStyled
          theme={{
            ...themeConWeb,
            glyphs: {
              // Temp hack for nutrient icons
              daily: { content: "'\\0046'" },
              facebook: { content: "'\\0041'" },
              hamburger: { content: "'\\0042'" },
              instagram: { content: "'\\0043'" },
              linkedin: { content: "'\\0044'" },
              meetings: { content: "'\\0045'" },
              twitter: { content: "'\\0047'" },
            },
          }}
        >
          <ThemeProvider theme={theme}>
            <Provider store={store}>
              <ProviderCheckout initialState={initialState} reducer={reducer}>
                <ConnectedRouter history={history}>
                  <Switch>
                    <PublicLayoutRoute
                      component={PageLogin}
                      path={loginRoute.path}
                      title={loginRoute.title}
                    />
                    <FooterLayoutRoute
                      component={PrivacyPolicyPage}
                      path={privacyPolicyRoute.path}
                      title={privacyPolicyRoute.title}
                    />
                    <FooterLayoutRoute
                      component={TermsOfUsePage}
                      path={termsAndConditionsRoute.path}
                      title={termsAndConditionsRoute.title}
                    />
                    <FooterLayoutRoute
                      component={TermsOfUseRewardsPage}
                      path={termsAndConditionsRewardsRoute.path}
                      title={termsAndConditionsRewardsRoute.title}
                    />
                    <PublicLayoutRoute
                      component={PageChangePassword}
                      path={changePasswordRoute.path}
                      title={changePasswordRoute.title}
                    />
                    <PublicLayoutRoute
                      component={PageForgotPassword}
                      path={forgotPasswordRoute.path}
                      title={forgotPasswordRoute.title}
                    />
                    <SimpleLayoutRoute
                      component={PageCompleteAccount}
                      path={completeAccountRoute.path}
                      title={completeAccountRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageSelectLocation}
                      overrideAuthRedirect
                      path={selectLocationRoute.path}
                      sidebarRight={[]}
                      title={selectLocationRoute.title}
                    />
                    <Route
                      component={HomeRedirect}
                      exact
                      path={homeRoute.path}
                      title={homeRoute.title}
                    />
                    <Route
                      component={HomeRedirect}
                      exact
                      path={rootRoute.path}
                      title={rootRoute.title}
                    />
                    <RouteLayoutPrivate
                      component={PageHome}
                      exact
                      overrideAuthRedirect
                      path={locationRoute.path}
                      showSubNav={true}
                      title={locationRoute.title}
                    />
                    <RouteLayoutPrivate
                      component={PagePlaceOrder}
                      exact
                      path={placeOrderRoute.path}
                      title={placeOrderRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageConfirmation}
                      path={confirmationRoute.path}
                      showSubNav={true}
                      title={confirmationRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageOrderHistory}
                      path={historyRoute.path}
                      title={historyRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageCheckout}
                      path={checkoutRoute.path}
                      sidebarRight={[CheckoutSidebar]}
                      title={checkoutRoute.title}
                    />
                    <RouteLayoutPrivate
                      component={PageCredits}
                      exact
                      overrideAuthRedirect
                      path={creditsRoute.path}
                      title={creditsRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageProfile}
                      path={profileRoute.path}
                      sidebarRight={[ProfileSidebar]}
                      title={profileRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageContact}
                      path={contactRoute.path}
                      title={contactRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageFriends}
                      path={friendsRoute.path}
                      sidebarRight={FriendRequest}
                      title={friendsRoute.title}
                    />
                    <PublicLayoutRoute
                      component={PageNoLocation}
                      path={noLocationRoute.path}
                      title={noLocationRoute.title}
                    />
                    <Logout path={logoutRoute.path} title={logoutRoute.title} />
                    <PublicLayoutRoute
                      component={PageMaintenance}
                      path={maintenanceRoute.path}
                      title={maintenanceRoute.title}
                    />
                    <PublicLayoutRoute
                      component={PageUnsubscribe}
                      path={unsubscribeRoute.path}
                      title={unsubscribeRoute.title}
                    />
                    <SimpleLayoutRoute
                      component={PageSlack}
                      path={slackRoute.path}
                      title={slackRoute.title}
                    />
                    <FooterLayoutRoute
                      component={PageSubscriptionTermsAndConditions}
                      path={subscriptionTermsAndConditionsRoute.path}
                      title={subscriptionTermsAndConditionsRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageGiftRedemption}
                      path={redeemGiftRoute.path}
                      title={redeemGiftRoute.title}
                    />
                    <FooterLayoutRoute
                      component={PageReferralTermsAndConditions}
                      path={referralTermsAndConditionsRoute.path}
                      title={referralTermsAndConditionsRoute.title}
                    />
                    <PublicLayoutRoute
                      component={PageReferral}
                      path={referralRoute.path}
                      title={referralRoute.title}
                    />
                    <PublicLayoutRoute
                      component={PageUI}
                      path={uiRoute.path}
                      title={uiRoute.title}
                    />
                    {/* This one goes last */}
                    <PublicLayoutRoute component={NotFound} title="Not found" />
                  </Switch>
                </ConnectedRouter>
              </ProviderCheckout>
            </Provider>
          </ThemeProvider>
        </ThemeProviderStyled>,
        root,
      )
    }
  } catch (ex) {
    logException(ex)
  }
}

// REDIRECT OLD URLS HERE
if (window.location.pathname.indexOf('/home/') > -1) {
  window.location = window.location.pathname.replace('/home', '')
} else if (!inIframe()) {
  renderApp()
}
